import * as Yup from 'yup';
import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroText,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { useSendDocumentLinkQuery } from '../../shared/hooks';
import { DataRoomDocument } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface SendDocumentFormProps {
	dataRoomGuid: string | undefined;
	document: DataRoomDocument | null;
	close: () => void;
}

export const SendDocumentForm: React.FC<SendDocumentFormProps> = ({ dataRoomGuid, document, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();

	const linkQuery = useSendDocumentLinkQuery(dataRoomGuid, document?.guid, {
		enabled: document != null,
		retry: false,
	});

	const sendFolderMutation = useMutation(
		async ({ values }: SendFolderLinkFormMutationVariables) => {
			const { data, error } = await getApiResponseData<DataRoomDocument>(
				api.post(CerebroApiEndpoints.DataRooms.SendLink(dataRoomGuid, document?.guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Email was sent for folder link.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	if (linkQuery.isLoading)
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={300} />
			</>
		);

	return (
		<CerebroDrawer open={document != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading>{document?.name}</CerebroHeading>
			<Formik
				validationSchema={SendLinkSchema}
				initialValues={{
					toList: [] as string[],
					subject: linkQuery.data?.subject ?? '',
					emailBody: linkQuery.data?.emailBody ?? '',
					linkType: 2,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(sendFolderMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel required id="toList">
										Lenders
									</CerebroLabel>
									{linkQuery.isSuccess && (
										<Field
											id="toList"
											as={CerebroMultiDropdown}
											name="toList"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('toList', errors, touched)}
										>
											{linkQuery.data.lenders
												.sort((a, b) => a.bankName.localeCompare(b.bankName))
												.map(user => (
													<CerebroMultiDropdownMenuItem
														key={'document-' + user.email}
														value={user.email}
													>
														{user.name} ({user.bankName})
													</CerebroMultiDropdownMenuItem>
												))}
										</Field>
									)}
									{FormikUtils.IsInvalid('toList', errors, touched) && (
										<CerebroText variant="error">{errors.toList}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel required id="subject">
										Subject
									</CerebroLabel>
									<Field
										as={CerebroInput}
										id="subject"
										name="subject"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('subject', errors, touched)}
									/>
									{FormikUtils.IsInvalid('subject', errors, touched) && (
										<CerebroText variant="error">{errors.subject}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel required id="emailBody">
										Email Body
									</CerebroLabel>
									<CerebroRichText
										value={values.emailBody ?? ''}
										onChange={updatedValue => setFieldValue('emailBody', updatedValue)}
									/>
									{FormikUtils.IsInvalid('emailBody', errors, touched) && (
										<CerebroText variant="error">{errors.emailBody}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Send
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type SendFolderLinkFormMutationVariables = FormikUtils.MutateOnSubmitVariables<SendFolderLinkFormValues>;
interface SendFolderLinkFormValues {
	toList: string[];
	subject: string;
	emailBody: string;
	linkType: number;
}
type SendFolderLinkFormValueErrors = FormikErrors<SendFolderLinkFormValues>;

const SendLinkSchema = Yup.object().shape({
	subject: Yup.string().required('Subject is required'),
	emailBody: Yup.string().required('Email body text is required'),
	toList: Yup.array().min(1, 'You must select at least one lender to send the link to.'),
});
